import React from "react";
import {
  Dialog,
  IconButton,
  Typography,
  Button,
  Box,
  makeStyles,
} from "@material-ui/core";
import { X } from "lucide-react";
import modalIcon from "../../assets/images/icons/modalIcon.png";

const useStyles = makeStyles(theme => ({
  paper: {
    backgroundColor: "#131313",
    borderRadius: "12px",
    padding: "40px",
    maxWidth: "550px",
    width: "100%",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: "20px 15px",
    },
  },
  closeButton: {
    position: "absolute",
    top: "16px",
    right: "16px",
    color: "#FFFFFF",
    padding: "8px",
  },
  iconWrapper: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "24px",
  },
  icon: {
    width: "80px",
    height: "auto",
  },
  title: {
    fontSize: "32px",
    fontWeight: 700,
    color: "#FFFFFF",
    textAlign: "center",
    marginBottom: "16px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "20px",
    },
  },
  message: {
    fontSize: "16px",
    fontWeight: 400,
    color: "rgba(255, 255, 255, 0.9)",
    textAlign: "center",
    marginBottom: "32px",
    lineHeight: 1.5,
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
    },
  },
  primaryButton: {
    backgroundColor: "#FE5A97",
    border: "1px solid #FE5A97",
    color: "#FFFFFF",
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 600,
    padding: "12px 20px",
    borderRadius: "8px",
    width: "100%",
    "&:hover": {
      backgroundColor: "#FF6BA8",
      borderColor: "#FF6BA8",
    },
  },
  cancelRejectionButton: {
    backgroundColor: "transparent",
    border: "1px solid #FE5A97",
    color: "#FE5A97",
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 600,
    padding: "12px 20px",
    borderRadius: "8px",
    width: "100%",
    marginTop: "12px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      borderColor: "#FE5A97",
    },
  },
}));

export interface RejectVideoRequestSuccessModalProps {
  open: boolean;
  onClose: () => void;
  /** Button label. Default: "Continue to Sugar cribs" */
  buttonText?: string;
  /** Called when the main button is clicked. Default: onClose */
  onButtonClick?: () => void;
  /** Called when "Cancel my rejection" is clicked. If not provided, button is not shown. */
  onCancelRejection?: () => void;
}

const RejectVideoRequestSuccessModal: React.FC<
  RejectVideoRequestSuccessModalProps
> = ({
  open,
  onClose,
  buttonText = "Continue to Sugar cribs",
  onButtonClick,
  onCancelRejection,
}) => {
  const classes = useStyles();
  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    } else {
      onClose();
    }
  };

  const handleCancelRejection = () => {
    onCancelRejection?.();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        className: classes.paper,
      }}
    >
      <IconButton className={classes.closeButton} onClick={onClose} size="small">
        <X size={20} />
      </IconButton>

      <Box className={classes.iconWrapper}>
        <img src={modalIcon} alt="Rejected" className={classes.icon} />
      </Box>

      <Typography className={classes.title}>
        You've rejected this request
      </Typography>
      <Typography className={classes.message}>
        We will notify your follower about it
      </Typography>
      <Button
        className={classes.primaryButton}
        onClick={handleButtonClick}
        fullWidth
      >
        {buttonText}
      </Button>
      {onCancelRejection && (
        <Button
          className={classes.cancelRejectionButton}
          onClick={handleCancelRejection}
          fullWidth
        >
          Cancel my rejection
        </Button>
      )}
    </Dialog>
  );
};

export default RejectVideoRequestSuccessModal;
